import { useQuery } from '@tanstack/react-query';
import {
  getLightspeedBusinessLocations,
  getLightspeedOAuthConnection,
} from './apiHelpers';

const BUSINESS_LOCATIONS_STALE_TIME_MS = 5 * 60 * 1000;

/** Prefix of every Lightspeed query of a restaurant, to reset or invalidate them together. */
export const lightspeedQueryKey = (restaurantId: string) => [
  'restaurants',
  restaurantId,
  'lightspeed',
];

export const lightspeedConnectionQueryKey = (restaurantId: string) => [
  ...lightspeedQueryKey(restaurantId),
  'connection',
];

export const lightspeedBusinessLocationsQueryKey = (restaurantId: string) => [
  ...lightspeedQueryKey(restaurantId),
  'business-locations',
];

export const useLightspeedConnection = (restaurantId: string) =>
  useQuery({
    queryKey: lightspeedConnectionQueryKey(restaurantId),
    queryFn: () => getLightspeedOAuthConnection(restaurantId),
  });

/**
 * The Lightspeed locations of the connected account. The connection card and the picker both
 * need them, and each request goes through a token check to Lightspeed, so they share one.
 * It doesn't retry or refetch on focus: a failure usually means the restaurant has to
 * reconnect, and asking again won't change that.
 */
export const useLightspeedBusinessLocations = (
  restaurantId: string,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useQuery({
    queryKey: lightspeedBusinessLocationsQueryKey(restaurantId),
    queryFn: async () =>
      (await getLightspeedBusinessLocations(restaurantId)).businessLocations,
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: BUSINESS_LOCATIONS_STALE_TIME_MS,
  });
